import React, { useState, useCallback, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquare } from '@fortawesome/free-solid-svg-icons'
import {
    Line,
    LineChart,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Label,
    Legend,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'
import { LoadingIndicator } from 'components'
import { alertSnackbarContentProps } from 'components/AlertSnackbar'

interface IChartLines {
    [key: string]: { name: string; enabled: boolean }
}

interface IMultiLineChartProps {
    data: any[]
    chartLines: string[]
    isLoading: boolean
    setAlertSnackbarMainProps: (value: alertSnackbarContentProps) => void
    setAlertSnackbarMainOpen: (status: boolean) => void
    isXAxisDate?: boolean
    xAxisDataKey?: string
    xAxisAngle?: boolean
}

const CustomTooltip = ({ active, payload, label }: any) => {
    const { clientTheme } = useThemeStyling()

    if (active && payload?.length) {
        return (
            <div
                className={'emp-multiLineChart-tooltipContainer'}
                style={{ fontSize: clientTheme.typography.body1.fontSize }}
            >
                <table style={{ borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr style={{ backgroundColor: '#b3b3b3' }}>
                            <td
                                colSpan={2}
                                className={
                                    'emp-multiLineChart-tooltipTableHeader'
                                }
                            >
                                {label}
                            </td>
                        </tr>
                        {payload.map((item: any, idx: number) => {
                            return (
                                <tr key={`key-${item.name}-${idx}`}>
                                    <td
                                        className={
                                            'emp-multiLineChart-tooltipTableRow'
                                        }
                                        style={{
                                            borderRight: '1px dotted #b3b3b3',
                                            textAlign: 'left',
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            size="xs"
                                            icon={faSquare}
                                            style={{
                                                marginRight: '5px',
                                                color: item.stroke,
                                            }}
                                        />
                                        {item.name}
                                    </td>
                                    <td
                                        className={
                                            'emp-multiLineChart-tooltipTableRow'
                                        }
                                        style={{
                                            textAlign: 'right',
                                        }}
                                    >
                                        {Number(item.value).toLocaleString(
                                            'en-US'
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        )
    }

    return null
}
const CustomTooltipDate = ({ active, payload, label }: any) => {
    const { clientTheme } = useThemeStyling()
    const dateFormat = 'yy-MM-dd'

    if (active && payload?.length) {
        return (
            <div
                className={'emp-multiLineChart-tooltipContainer'}
                style={{ fontSize: clientTheme.typography.body1.fontSize }}
            >
                <table style={{ borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr style={{ backgroundColor: '#b3b3b3' }}>
                            <td
                                colSpan={2}
                                className={
                                    'emp-multiLineChart-tooltipTableHeader'
                                }
                            >
                                {format(parseISO(label), dateFormat)}
                            </td>
                        </tr>
                        {payload.map((item: any, idx: number) => {
                            return (
                                <tr key={`key-${item.name}-${idx}`}>
                                    <td
                                        className={
                                            'emp-multiLineChart-tooltipTableRow'
                                        }
                                        style={{
                                            borderRight: '1px dotted #b3b3b3',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            size="xs"
                                            icon={faSquare}
                                            style={{
                                                marginRight: '5px',
                                                color: item.stroke,
                                            }}
                                        />
                                        {item.name}
                                    </td>
                                    <td
                                        className={
                                            'emp-multiLineChart-tooltipTableRow'
                                        }
                                        style={{
                                            textAlign: 'right',
                                        }}
                                    >
                                        {Number(item.value).toLocaleString(
                                            'en-US'
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        )
    }

    return null
}

const MultiLineChart = ({
    data,
    chartLines,
    isLoading,
    setAlertSnackbarMainProps,
    setAlertSnackbarMainOpen,
    isXAxisDate,
    xAxisDataKey = 'date',
    xAxisAngle = false,
}: IMultiLineChartProps): React.ReactElement => {
    const { clientTheme } = useThemeStyling()
    const [enabledLine, setEnabledLine] = useState<IChartLines>({})

    const height = 350
    const dateFormat = 'yy-MM-dd'
    const colorPalette = clientTheme.charts.areaCharts.newAlertsByDateChart

    useEffect(() => {
        if (!chartLines.length) return

        const updatedChartLines = chartLines.reduce(
            (acc: IChartLines, service: string) => {
                acc[service] = { name: service, enabled: true }
                return acc
            },
            {}
        )

        setEnabledLine(updatedChartLines)
    }, [chartLines])

    const handleLegend = useCallback(
        (props: any) => {
            const { dataKey, inactive } = props
            const updatedEnabledLines = {
                ...enabledLine,
                [dataKey]: { name: dataKey, enabled: inactive },
            } as IChartLines
            const isDataVisible = Object.values(updatedEnabledLines).some(
                (dataPoint) => dataPoint?.enabled
            )
            if (!isDataVisible) {
                setAlertSnackbarMainProps({
                    message: 'At least one data key must be visible.',
                    title: 'Error.',
                    intent: 'error',
                })
                setAlertSnackbarMainOpen(true)
            } else {
                setEnabledLine(updatedEnabledLines)
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [enabledLine]
    )

    const renderLegend = (props: any) => {
        const { payload } = props

        return (
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '2px 10px',
                    padding: '4px 22px',
                    marginLeft: '30px',
                }}
            >
                {payload.map((entry: any, index: number) => (
                    <div
                        key={`item-${entry.value}-${index}`}
                        onClick={() => handleLegend(entry)}
                    >
                        <FontAwesomeIcon
                            size="xs"
                            icon={faSquare}
                            style={{
                                marginRight: '5px',
                                color: entry.color,
                                opacity: enabledLine[entry.value].enabled
                                    ? 1
                                    : 0.5,
                            }}
                        />
                        <span
                            style={{
                                fontSize: clientTheme.typography.body1.fontSize,
                                opacity: enabledLine[entry.value].enabled
                                    ? 1
                                    : 0.5,
                            }}
                        >
                            {entry.value}
                        </span>
                    </div>
                ))}
            </div>
        )
    }

    return (
        <div>
            {!isLoading ? (
                <div>
                    {data.length ? (
                        <div>
                            <ResponsiveContainer height={height} width="100%">
                                <LineChart
                                    data={data}
                                    margin={{
                                        right: xAxisAngle ? 60 : 10,
                                        left: -10,
                                        bottom: 5,
                                        top: 5,
                                    }}
                                >
                                    {xAxisAngle ? (
                                        <XAxis
                                            dataKey={xAxisDataKey}
                                            angle={45}
                                            textAnchor="start"
                                            height={70}
                                            interval={0}
                                            tickFormatter={
                                                isXAxisDate
                                                    ? (date: string): string =>
                                                          format(
                                                              parseISO(date),
                                                              dateFormat
                                                          )
                                                    : undefined
                                            }
                                            tick={{
                                                fontSize:
                                                    clientTheme.typography.label
                                                        .fontSize,
                                            }}
                                        />
                                    ) : (
                                        <XAxis
                                            dataKey={xAxisDataKey}
                                            tickFormatter={
                                                isXAxisDate
                                                    ? (date: string): string =>
                                                          format(
                                                              parseISO(date),
                                                              dateFormat
                                                          )
                                                    : undefined
                                            }
                                            tick={{
                                                fontSize:
                                                    clientTheme.typography.label
                                                        .fontSize,
                                            }}
                                        />
                                    )}
                                    <YAxis
                                        interval={0}
                                        tick={{
                                            fontSize:
                                                clientTheme.typography.label
                                                    .fontSize,
                                        }}
                                    />
                                    {isXAxisDate ? (
                                        <Tooltip
                                            content={<CustomTooltipDate />}
                                        />
                                    ) : (
                                        <Tooltip content={<CustomTooltip />} />
                                    )}
                                    {Object.values(enabledLine).map(
                                        (dataPoint, idx) => {
                                            const dataColor =
                                                colorPalette[
                                                    idx % colorPalette.length
                                                ]
                                            return (
                                                <Line
                                                    key={`key-${dataPoint.name}-${idx}`}
                                                    type="monotone"
                                                    dataKey={dataPoint.name}
                                                    stroke={dataColor.stroke}
                                                    fill={dataColor.fill}
                                                    hide={!dataPoint.enabled}
                                                    dot={{
                                                        stroke:
                                                            dataColor.stroke,
                                                        fill: dataColor.stroke,
                                                    }}
                                                />
                                            )
                                        }
                                    )}
                                    <Legend
                                        content={renderLegend}
                                        layout="horizontal"
                                        align="center"
                                        verticalAlign="bottom"
                                        wrapperStyle={{
                                            fontSize:
                                                clientTheme.typography.body1
                                                    .fontSize,

                                            color: '#535353',
                                        }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height={height}>
                            <LineChart
                                data={[]}
                                margin={{
                                    right: 10,
                                    left: -30,
                                    bottom: 5,
                                    top: 5,
                                }}
                            >
                                <XAxis ticks={['No Data']}>
                                    <Label
                                        value="No Data"
                                        offset={-150}
                                        position="insideTop"
                                        style={{
                                            fontSize: '14px',
                                        }}
                                    />
                                </XAxis>
                                <YAxis
                                    domain={[0, 100]}
                                    tick={{
                                        fontSize:
                                            clientTheme.typography.label
                                                .fontSize,
                                    }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>
            ) : (
                <div
                    style={{
                        height: 200,
                    }}
                    className={'emp-multiLineChart-loadingContainer'}
                >
                    <LoadingIndicator />
                </div>
            )}
        </div>
    )
}

export default MultiLineChart
